<template>
  <a-table
    size="middle"
    :columns="quickActionsColumns"
    :data-source="answersData?.results"
    :pagination="pagination"
    :loading="loadingTable"
    @change="handleChange"
  >
    <template #bodyCell="{ text, column, record }">
      <template v-if="column.key === 'file'">
        <span :class="{ empty: !text }">
          {{ getFileTypeName(record.type_file) }}
        </span>
      </template>

      <template v-if="column.key === 'portfolios'">
        <div class="queue">
          <span>
            {{ text.length === 1 ? text.at(0)?.name : `${text.length} campañas` }}
          </span>

          <a-popover v-if="text.length > 1" :title="`Campañas asignadas (${text.length})`">
            <template #content>
              <ul>
                <li v-for="portfolio in text.slice(0, 5)" :key="portfolio.id">{{ portfolio.name }}</li>
              </ul>
              <template v-if="text.length > 5">
                <a-divider class="divider" />
                <a class="show" @click="handleListPortfolios(text)"
                  > <EyeOutlined style="margin-right: 5px"/> Ver {{ text.length - 5 }} más</a
                >
              </template>
            </template>

            <InfoCircleOutlined style="margin-left: 5px" />
          </a-popover>
        </div>
      </template>

      <template v-if="column.key === 'queues'">
        <div class="queue">
          <span>
            {{ text.length === 1 ? text.at(0)?.name : `${text.length} colas asignadas` }}
          </span>

          <a-popover v-if="text.length > 1" :title="`Colas vinculadas (${text.length})`">
            <template #content>
              <ul>
                <li v-for="queue in text.slice(0, 5)" :key="queue.id">{{ queue.name }}</li>
              </ul>
              <template v-if="text.length > 5">
                <a-divider class="divider" />
                <a class="show" @click="handleListQueues(text)"
                  > <EyeOutlined style="margin-right: 5px"/> Ver {{ text.length - 5 }} más</a
                >
              </template>
            </template>

            <InfoCircleOutlined style="margin-left: 5px" />
          </a-popover>
        </div>
      </template>

      <template v-if="column.key === 'actions'">
        <div class="actions">
          <a-button type="link" class="button" @click="handleOpen(record)">
            <EditOutlined />
          </a-button>

          <a-button type="link" class="button" @click="handleDelete(record)">
            <DeleteOutlined />
          </a-button>
        </div>
      </template>
    </template>
  </a-table>

  <ListQueuesModal v-if="visibleListQueueModal" v-model:visible="visibleListQueueModal" :queues="queues" />
  <ListPortfoliosModal v-if="visibleListPortfolioModal" v-model:visible="visibleListPortfolioModal" :portfolios="portfolios" />

</template>

<script setup lang="ts">
import { computed, createVNode, onMounted, ref, watch } from "vue";
import { message, Modal, type TableProps } from "ant-design-vue";
import {
  InfoCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
  EyeOutlined,
} from "@ant-design/icons-vue";

import { quickActionsColumns } from "@/app/platformSettings/utils/columns/quickActions";
import type { Pagination } from "@/@types/utils";
import { Queue, Portfolio } from "@/@types/global";
import type { AnswerMediaType, QuickAnswer } from "@/@types/platformSettings/quickAnswers";
import { quickAnswersService } from "../../services/QuickActionsService";
import { debounce } from "lodash";
import ListQueuesModal from "@/app/platformSettings/components/modal/ListQueuesModal.vue";
import ListPortfoliosModal from "@/app/platformSettings/components/modal/ListPortfoliosModal.vue";

const props = defineProps<{ search: string }>();

const emit = defineEmits<{ edit: [record: QuickAnswer] }>();

const answersData = ref<Pagination<QuickAnswer>>();
const currentPage = ref(1);

const loadingTable = ref(false);
const visibleListQueueModal = ref(false);
const queues = ref<Queue[]>([]);

const visibleListPortfolioModal = ref(false);
const portfolios = ref<Portfolio[]>([]);

const fetchData = async () => {
  loadingTable.value = true;

  answersData.value = await quickAnswersService.getList({
    page: currentPage.value,
    title: props.search,
  });

  loadingTable.value = false;
};

onMounted(() => fetchData());

const pagination = computed(() => ({
  total: answersData.value?.count,
  pageSize: 6,
  showSizeChanger: false,
}));

const handleChange: TableProps["onChange"] = async (page) => {
  currentPage.value = page.current || 1;
  fetchData();
};

watch(
  () => props.search,
  debounce(() => {
    currentPage.value = 1;
    fetchData();
  }, 200)
);

defineExpose({ fetchData });

const getFileTypeName = (typeName: AnswerMediaType) => {
  if (typeName === "image") return "Imagen";
  if (typeName === "video") return "Video";
  if (typeName === "file") return "Documento";

  return "Sin multimedia";
};

const handleOpen = (record: QuickAnswer) => {
  emit("edit", record);
};

const handleDelete = (record: QuickAnswer) => {
  Modal.confirm({
    centered: true,
    icon: createVNode(QuestionCircleOutlined),
    title: `¿Estás seguro de eliminar la respuesta rápida “${record.title}”?`,
    content: "Recuerda que ya no podrá ser utilizada por tus usuarios en el módulo de Workspace.",
    okText: "Si, eliminar",
    cancelText: "No, cancelar",
    onOk: async () => {
      await quickAnswersService.delete(record.id);
      await fetchData();
      message.success("Respuesta rápida eliminada con éxito.");
    },
  });
};

const handleListQueues = (list: Queue[]) => {
  queues.value = list ?? [];       
  visibleListQueueModal.value = true;
}

const handleListPortfolios = (list: Portfolio[]) => {
  portfolios.value = list ?? [];       
  visibleListPortfolioModal.value = true;
}
</script>

<style scoped lang="sass">
.empty
  color: $gray-6
  font-style: italic

.button
  padding: 0
  font-size: inherit

.queues
  display: flex
  justify-content: space-between

.actions
  display: flex
  gap: 16px
  font-size: 18px

ul
  margin: 12px 0 4px 0

li
  line-height: 22px
  padding-bottom: 12px
  color: #262626E0

.show
  display: flex
  align-items: center
  justify-content: center
  padding: 12px 0
  width: 100%

.divider
  position: absolute
  left: 0
  bottom: 46px
  margin: 12px 0

</style>
